/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './StartIcon';
import { toolTipProps } from 'componentregistry/utils/commonProps';

const StartIconManager: ComponentManagerFn<'StartIcon'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'StartIcon'>(platform, 'StartIcon'),
    type: 'StartIcon',
    name: 'Start Icon',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'Popover'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      icon: {
        label: 'Icon',
        type: 'iconselect',
        helpText: 'Default Icon to be used when the attribute value is null',
      },
      color: {
        label: 'Color',
        type: 'ColorExpression',
        placeholder: 'e.g. primary or #444',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.icon?.iconName ?? 'Choose icon!',
    category: 'Forms',
    icon: { iconName: 'icons' },
    description: `**Overview**<br/>
The Start Icon component is a decorative and functional element designed to be placed at the leading edge (left side) of input fields. It acts as a visual prefix, providing immediate context to the user about the expected input type before they even begin typing.
Use this component to standardize the look of your forms—for example, placing a "User" icon in a username field or an "Envelope" icon in an email field.

**1. Configuration & Styling**<br/>
You can customize the icon to match the purpose of the input field.
* **Icon:** Select the specific visual icon to display (e.g., "user", "envelope", "lock"). This icon acts as the visual anchor for the field.
* **Color:** Defines the color of the icon via a **ColorExpression**. Use theme variables (e.g., **primary**, **secondary**) or standard hex codes (e.g., "#666") to blend the icon with your form design.
* **Tooltip:** Supports standard tooltip properties. This is useful for providing a hint when the user hovers over the symbol (e.g., "Please enter your work email").

**2. Interactivity**<br/>
While often used just for aesthetics, the Start Icon can be made interactive.
* **Triggers:** Supports the **TriggerOnClick** child component, effectively turning the icon into a button.
* **Popover:** Supports the **Popover** child component. Clicking the icon can reveal a small menu or informational popup (e.g., a "Why do we need this?" help bubble).

**3. Best Use Cases**<br/>
* **Contextual Cues:** Placing an "@" symbol or "envelope" icon in an Email field.
* **Security:** Placing a "lock" icon at the start of a Password field to reinforce safety.
* **Identity:** Placing a "user" or "id-badge" icon in a Login field.
* **Formatting:** Using a specific symbol to indicate a required format (e.g., a "phone" icon).
* **Search Bars:** Placing the magnifying glass icon on the left (the industry-standard pattern) for quick recognition.`,
  };
};

export default StartIconManager;
